import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface CityTour {
  id: string;
  name: string;
  city: string;
  country: string;
  style: string;
  description?: string;
  image?: string;
  latitude?: number;
  longitude?: number;
  duration?: string;
  museum_count?: number;
}

export const useCityTours = () => {
  const [tours, setTours] = useState<CityTour[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    const loadTours = async () => {
      try {
        const { data, error } = await supabase
          .from('city_tours')
          .select('*')
          .eq('is_public', true)
          .order('created_at', { ascending: false });

        if (error) throw error;

        // Map tours immediately without cleaning (cleaning should be done on creation/update, not on read)
        const mappedTours: CityTour[] = (data || []).map(tour => ({
          id: tour.id,
          name: tour['Nom ville'],
          city: tour['Nom ville'],
          country: tour.country,
          style: tour.style || '',
          description: tour.description,
          image: (tour as any).Image || undefined,
          latitude: tour.latitude,
          longitude: tour.longitude,
          duration: tour.duration,
          museum_count: tour.museum_count
        }));

        setTours(mappedTours);
      } catch (error) {
        console.error('Error loading tours:', error);
        toast({
          title: 'Erreur de chargement',
          description: 'Impossible de charger les tours.',
          variant: 'destructive'
        });
      } finally {
        setLoading(false);
      }
    };

    loadTours();
  }, [toast]);

  const filterToursByStyle = (style: string) => {
    return tours.filter(tour => 
      tour.style.toLowerCase().includes(style.toLowerCase())
    );
  };

  const filterToursByCountry = (country: string) => {
    return tours.filter(tour => 
      tour.country.toLowerCase() === country.toLowerCase()
    );
  };

  return {
    tours,
    loading,
    filterToursByStyle,
    filterToursByCountry
  };
};